import { createAsyncThunk } from '@reduxjs/toolkit';
import { myFirebaseAuth } from '../../firebase/config';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { firebaseFileUploadHelper, getFileNameExtension } from '../../utils/helpers';
import { AppFile, FiservCardDetails } from '@dill/dill-shared';

export const createPaymentRequest = createAsyncThunk(
  'paymentRequests/createPaymentRequest',
  async (
    {
      buyerId,
      supplierId,
      email,
      paymentAmount,
      buyerTakeRate,
      notes,
      attachmentsFiles,
      whoPaysFee,
      is3DSecure,
      phoneNumber,
      deliveryMethod,
      disabledPaymentTypes,
    }: {
      buyerId: string;
      supplierId: string;
      email: string | null;
      paymentAmount: number;
      buyerTakeRate: number;
      whoPaysFee: 'SUPPLIER' | 'BUYER' | 'BOTH';
      notes: string;
      attachmentsFiles?: File[];
      is3DSecure: boolean;
      phoneNumber?: string | null;
      deliveryMethod?: 'EMAIL' | 'SMS' | 'BOTH';
      disabledPaymentTypes?: ('CREDIT_CARD' | 'DEBIT_CARD' | 'BANK')[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const attachedFile: { name: string; url: string }[] = [];
      if (attachmentsFiles && attachmentsFiles.length > 0) {
        for (let index = 0; index < attachmentsFiles.length; index++) {
          const element = attachmentsFiles[index];
          const fileExtension = getFileNameExtension({
            fileName: element.name,
            isIncludePeriod: true,
          });
          const blobUrl = URL.createObjectURL(element);
          const fileUrl = await firebaseFileUploadHelper({
            blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-attachment-${
              index + 1
            }${fileExtension}`,
            storagePath: `paymentRequests/${supplierId}`,
          });
          if (fileUrl) {
            attachedFile.push({
              name: element.name,
              url: fileUrl,
            });
          }
        }
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentRequests/createPaymentRequest`,
        {
          buyerId,
          email,
          paymentAmount,
          buyerTakeRate,
          whoPaysFee,
          notes,
          attachments: attachedFile,
          is3DSecure,
          phoneNumber,
          deliveryMethod,
          disabledPaymentTypes,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getPaymentRequests = createAsyncThunk(
  'paymentRequests/getPaymentRequests',
  async ({ supplierId, buyerId }: { supplierId: string; buyerId?: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const queryParams = buyerId ? `?buyerId=${buyerId}` : '';
      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentRequests/getPaymentRequests${queryParams}`,

        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const getPaymentRequestDetailsForPayment = createAsyncThunk(
  'paymentRequests/getPaymentRequestDetailsForPayment',
  async ({ paymentRequestId }: { paymentRequestId: string }, thunkApi) => {
    try {
      const response = await axios.get<any>(
        `${BASE_API_URL}/generics/paymentRequests/getPaymentRequestDetails/${paymentRequestId}`,

        {}
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const paymentRequestPayment = createAsyncThunk(
  'paymentRequests/paymentRequestPayment',
  async (
    {
      confirmationEmail,
      paymentRequestId,
      paymentMethodName,
      paymentMethodId,
      amountPaid,
      idempotencyKey,
    }: {
      confirmationEmail: string;
      paymentRequestId: string;
      paymentMethodId: string;
      paymentMethodName: string;
      amountPaid: number;
      /**
       * Stable per-attempt key. Must be reused verbatim when retrying the same
       * payment so the server can collapse duplicates instead of charging twice.
       */
      idempotencyKey?: string;
    },
    thunkApi
  ) => {
    try {
      const response = await axios.post<any>(
        `${BASE_API_URL}/generics/paymentRequests/pay`,
        {
          paymentRequestId,
          paymentMethodId,
          amountPaid,
          confirmationEmail,
          paymentMethodName,
        },
        { headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {} }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg} `;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
export const paymentRequestFiservPayment = createAsyncThunk(
  'paymentRequests/paymentRequestFiservPayment',
  async (
    {
      confirmationEmail,
      paymentRequestId,
      amountPaid,
      fiservCardDetails,
      idempotencyKey,
    }: {
      confirmationEmail: string;
      paymentRequestId: string;
      amountPaid: number;
      fiservCardDetails: FiservCardDetails;
      /** Stable per-attempt key; reuse verbatim when retrying the same payment. */
      idempotencyKey?: string;
    },
    thunkApi
  ) => {
    try {
      const response = await axios.post<any>(
        `${BASE_API_URL}/generics/paymentRequests/fiservPay`,
        {
          paymentRequestId,
          amountPaid,
          confirmationEmail,
          fiservCardDetails,
        },
        { headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {} }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg} `;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const paymentRequestDwollaPayment = createAsyncThunk(
  'paymentRequests/paymentRequestDwollaPayment',
  async (
    {
      confirmationEmail,
      paymentRequestId,
      amountPaid,
      plaidToken,
      fundingSourceName,
      bankAccountType,
      idempotencyKey,
    }: {
      confirmationEmail: string;
      paymentRequestId: string;
      amountPaid: number;
      plaidToken: string;
      fundingSourceName: string;
      bankAccountType: string;
      /** Stable per-attempt key; reuse verbatim when retrying the same payment. */
      idempotencyKey?: string;
    },
    thunkApi
  ) => {
    try {
      const response = await axios.post<any>(
        `${BASE_API_URL}/generics/paymentRequests/dwollaPay`,
        {
          paymentRequestId,
          amountPaid,
          confirmationEmail,
          plaidToken,
          fundingSourceName,
          bankAccountType,
        },
        { headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {} }
      );
      return {
        success: true,
        data: response.data,
        // Set by the idempotency middleware when this answer is a replay of an
        // earlier attempt rather than a payment processed just now.
        isReplay: response.headers?.['idempotent-replay'] === 'true',
      };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg} `;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const resendPaymentRequest = createAsyncThunk(
  'paymentRequests/resendPaymentRequest',
  async (
    {
      supplierId,
      paymentRequestId,
    }: {
      supplierId: string;
      paymentRequestId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentRequests/resendPaymentRequest/${paymentRequestId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const updatePaymentRequest = createAsyncThunk(
  'paymentRequests/updatePaymentRequest',
  async (
    {
      paymentRequestId,
      buyerId,
      supplierId,
      email,
      buyerTakeRate,
      paymentAmount,
      notes,
      attachmentsFiles,
      whoPaysFee,
      currentAttachment,
      is3DSecure,
      phoneNumber,
      deliveryMethod,
      disabledPaymentTypes,
    }: {
      paymentRequestId: string;
      buyerId: string;
      supplierId: string;
      email: string | null;
      paymentAmount: number;
      buyerTakeRate: number;
      whoPaysFee: 'SUPPLIER' | 'BUYER' | 'BOTH';
      notes: string;
      attachmentsFiles?: File[];
      currentAttachment: AppFile[];
      is3DSecure: boolean;
      phoneNumber?: string | null;
      deliveryMethod?: 'EMAIL' | 'SMS' | 'BOTH';
      disabledPaymentTypes?: ('CREDIT_CARD' | 'DEBIT_CARD' | 'BANK')[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const currentAttachmentFiles = currentAttachment
        .filter((item) => item.url !== '')
        .map((item) => {
          return {
            name: item.name ?? '',
            url: item.url ?? '',
          };
        });

      const attachedFile: { name: string; url: string }[] = [];
      if (attachmentsFiles && attachmentsFiles.length > 0) {
        for (let index = 0; index < attachmentsFiles.length; index++) {
          const element = attachmentsFiles[index];
          const fileExtension = getFileNameExtension({
            fileName: element.name,
            isIncludePeriod: true,
          });
          const blobUrl = URL.createObjectURL(element);
          const fileUrl = await firebaseFileUploadHelper({
            blobUrl,
            fileNameWithExtension: `${new Date().getTime()}-attachment-${
              index + 1
            }${fileExtension}`,
            storagePath: `paymentRequests/${supplierId}`,
          });
          if (fileUrl) {
            attachedFile.push({
              name: element.name,
              url: fileUrl,
            });
          }
        }
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentRequests/updatePaymentRequest/${paymentRequestId}`,
        {
          buyerId,
          emails: email ? email.split(', ') : [],
          buyerTakeRate,
          totalAmount: paymentAmount,
          balance: paymentAmount,
          whoPaysFee,
          notes,
          attachments: [...attachedFile, ...currentAttachmentFiles],
          is3DSecure,
          phoneNumber,
          deliveryMethod,
          disabledPaymentTypes,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);

export const deletePaymentRequest = createAsyncThunk(
  'paymentRequests/deletePaymentRequest',
  async (
    {
      supplierId,
      paymentRequestId,
    }: {
      supplierId: string;
      paymentRequestId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.delete<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentRequests/deletePaymentRequest/${paymentRequestId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
export const getSupplierPaymentRequest = createAsyncThunk(
  'paymentRequests/getSupplierPaymentRequest',
  async (
    {
      supplierId,
      paymentRequestId,
    }: {
      supplierId: string;
      paymentRequestId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/paymentRequests/getSupplierPaymentRequestDetails/${paymentRequestId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return { success: true, data: response.data };
    } catch (error: any) {
      console.log(error);
      console.log(error?.message);
      let errorMessage = '';
      if (error?.response?.data?.errors) {
        errorMessage = [...error?.response?.data?.errors].reduce((prev, curr) => {
          if (curr.msg) {
            return `${prev} ${curr.msg}`;
          }
          return prev;
        }, '');
      } else {
        errorMessage = error?.message;
      }
      return thunkApi.rejectWithValue(errorMessage);
    }
  }
);
